import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LineChart } from '@mui/x-charts/LineChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Cases = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const chartRef = useRef(null);
  
  // State
  const [fallzahlenData, setFallzahlenData] = useState(null);
  const [fallzahlenLoading, setFallzahlenLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  
  // Kurven-Toggles
  const [showMyReports, setShowMyReports] = useState(true);
  const [showForeignSignedReports, setShowForeignSignedReports] = useState(true);
  const [showIndicationApprovals, setShowIndicationApprovals] = useState(true);
  const [showReleasedReports, setShowReleasedReports] = useState(true);

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Load Fallzahlen data on mount
  useEffect(() => {
    loadFallzahlenData();
  }, []);

  // Shift + Mausrad zum Wechseln der Zeiträume
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey) {
        event.preventDefault();
        
        const timeFilters = ['date', 'week', 'month', 'quarter', 'year'];
        const currentIndex = timeFilters.indexOf(timeFilter);
        
        let newIndex;
        if (event.deltaY < 0) {
          newIndex = (currentIndex + 1) % timeFilters.length;
        } else {
          newIndex = (currentIndex - 1 + timeFilters.length) % timeFilters.length;
        }
        
        setTimeFilter(timeFilters[newIndex]);
        setStartPeriod('');
        setEndPeriod('');
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter]);

  const loadFallzahlenData = async () => {
    setFallzahlenLoading(true);
    try {
      const response = await fetch(createApiUrl('statistics/personal/fallzahlen'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFallzahlenData(data);
      } else {
        showError(t('statistics.personal.fallzahlen.loadError', 'Fehler beim Laden der Fallzahlen'), 'ERR-STATS-001');
      }
    } catch (error) {
      console.error('Error loading Fallzahlen data:', error);
      showError(t('statistics.personal.fallzahlen.loadError', 'Fehler beim Laden der Fallzahlen'), 'ERR-STATS-002');
    } finally {
      setFallzahlenLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    setTimeFilter(event.target.value);
    setStartPeriod('');
    setEndPeriod('');
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  // CSV Export Function
  const handleCsvDownload = () => {
    if (!fallzahlenData) return;

    const getChartData = () => {
      const getDataForCategory = (category) => {
        let data = [];
        switch (timeFilter) {
          case 'date':
            data = category.by_date || [];
            break;
          case 'week':
            data = category.by_week || [];
            break;
          case 'month':
            data = category.by_month || [];
            break;
          case 'quarter':
            data = category.by_quarter || [];
            break;
          case 'year':
            data = category.by_year || [];
            break;
          default:
            data = category.by_month || [];
        }
        return data;
      };

      let myReportsData = getDataForCategory(fallzahlenData.my_reports);
      let foreignSignedData = getDataForCategory(fallzahlenData.foreign_signed_reports);
      let indicationData = getDataForCategory(fallzahlenData.indication_approvals);
      let releasedData = getDataForCategory(fallzahlenData.released_reports);

      const allPeriods = new Set([
        ...myReportsData.map(d => d.period),
        ...foreignSignedData.map(d => d.period),
        ...indicationData.map(d => d.period),
        ...releasedData.map(d => d.period)
      ]);

      const mergedData = Array.from(allPeriods).sort().map(period => {
        const myReport = myReportsData.find(d => d.period === period);
        const foreignSigned = foreignSignedData.find(d => d.period === period);
        const indication = indicationData.find(d => d.period === period);
        const released = releasedData.find(d => d.period === period);

        return {
          period,
          myReportsCount: myReport?.count || 0,
          foreignSignedCount: foreignSigned?.count || 0,
          indicationCount: indication?.count || 0,
          releasedCount: released?.count || 0
        };
      });

      let filteredData = mergedData;
      if (startPeriod && endPeriod) {
        const startIdx = mergedData.findIndex(d => d.period === startPeriod);
        const endIdx = mergedData.findIndex(d => d.period === endPeriod);
        if (startIdx !== -1 && endIdx !== -1) {
          filteredData = mergedData.slice(startIdx, endIdx + 1);
        }
      } else if (startPeriod) {
        const startIdx = mergedData.findIndex(d => d.period === startPeriod);
        if (startIdx !== -1) {
          filteredData = mergedData.slice(startIdx);
        }
      } else if (endPeriod) {
        const endIdx = mergedData.findIndex(d => d.period === endPeriod);
        if (endIdx !== -1) {
          filteredData = mergedData.slice(0, endIdx + 1);
        }
      }

      return filteredData;
    };

    const formatPeriod = (period) => {
      if (!period) return period;
      try {
        switch (timeFilter) {
          case 'date':
            if (!isFormatConfigLoaded) return period;
            return formatDateSync(period);
          case 'week':
            const weekMatch = period.match(/(\d{4})-W(\d{2})/);
            if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
            return period;
          case 'month':
            const monthMatch = period.match(/(\d{4})-(\d{2})/);
            if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
            return period;
          case 'quarter':
            const quarterMatch = period.match(/(\d{4})-Q(\d)/);
            if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
            return period;
          case 'year':
            return period;
          default:
            return period;
        }
      } catch (error) {
        return period;
      }
    };

    const chartData = getChartData();

    // Build CSV - nur aktive Serien exportieren
    let csv = '\uFEFF'; // BOM for Excel UTF-8
    csv += '"' + t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum') + '";';
    if (showIndicationApprovals) csv += '"' + t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen') + '";';
    if (showMyReports) csv += '"' + t('statistics.personal.pdf.composedReports', 'Verfasste Befunde') + '";';
    if (showForeignSignedReports) csv += '"' + t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde') + '";';
    if (showReleasedReports) csv += '"' + t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde') + '";';
    csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon und füge Newline hinzu

    chartData.forEach(row => {
      csv += '="' + formatPeriod(row.period) + '";';
      if (showIndicationApprovals) csv += row.indicationCount + ';';
      if (showMyReports) csv += row.myReportsCount + ';';
      if (showForeignSignedReports) csv += row.foreignSignedCount + ';';
      if (showReleasedReports) csv += row.releasedCount + ';';
      csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon und füge Newline hinzu
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `statistik_${currentUser?.id}_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    if (!fallzahlenData || !chartRef.current) return;

    try {
      // Farben dynamisch aus CSS-Variablen auslesen
      const rootStyles = getComputedStyle(document.documentElement);
      const chartColor1 = rootStyles.getPropertyValue('--rd-chart-1').trim();
      const chartColor2 = rootStyles.getPropertyValue('--rd-chart-2').trim();
      const chartColor3 = rootStyles.getPropertyValue('--rd-chart-3').trim();
      const chartColor4 = rootStyles.getPropertyValue('--rd-chart-4').trim();

      const chartElement = chartRef.current;
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        backgroundColor: 'var(--rd-bg-paper)'
      });
      const chartImage = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      const getChartDataForPeriod = () => {
        const getDataForCategory = (category) => {
          let data = [];
          switch (timeFilter) {
            case 'date':
              data = category.by_date || [];
              break;
            case 'week':
              data = category.by_week || [];
              break;
            case 'month':
              data = category.by_month || [];
              break;
            case 'quarter':
              data = category.by_quarter || [];
              break;
            case 'year':
              data = category.by_year || [];
              break;
            default:
              data = category.by_month || [];
          }
          return data;
        };

        let myReportsData = getDataForCategory(fallzahlenData.my_reports);
        let foreignSignedData = getDataForCategory(fallzahlenData.foreign_signed_reports);
        let indicationData = getDataForCategory(fallzahlenData.indication_approvals);
        let releasedData = getDataForCategory(fallzahlenData.released_reports);

        const allPeriods = new Set([
          ...myReportsData.map(d => d.period),
          ...foreignSignedData.map(d => d.period),
          ...indicationData.map(d => d.period),
          ...releasedData.map(d => d.period)
        ]);

        const mergedData = Array.from(allPeriods).sort().map(period => {
          const myReport = myReportsData.find(d => d.period === period);
          const foreignSigned = foreignSignedData.find(d => d.period === period);
          const indication = indicationData.find(d => d.period === period);
          const released = releasedData.find(d => d.period === period);

          return {
            period,
            myReportsCount: myReport?.count || 0,
            foreignSignedCount: foreignSigned?.count || 0,
            indicationCount: indication?.count || 0,
            releasedCount: released?.count || 0
          };
        });

        let filteredData = mergedData;
        if (startPeriod && endPeriod) {
          const startIdx = mergedData.findIndex(d => d.period === startPeriod);
          const endIdx = mergedData.findIndex(d => d.period === endPeriod);
          if (startIdx !== -1 && endIdx !== -1) {
            filteredData = mergedData.slice(startIdx, endIdx + 1);
          }
        } else if (startPeriod) {
          const startIdx = mergedData.findIndex(d => d.period === startPeriod);
          if (startIdx !== -1) {
            filteredData = mergedData.slice(startIdx);
          }
        } else if (endPeriod) {
          const endIdx = mergedData.findIndex(d => d.period === endPeriod);
          if (endIdx !== -1) {
            filteredData = mergedData.slice(0, endIdx + 1);
          }
        }

        return filteredData;
      };

      const chartData = getChartDataForPeriod();

      const formatPeriod = (period) => {
        if (!period) return '';
        try {
          switch (timeFilter) {
            case 'date':
              if (!isFormatConfigLoaded) return period;
              return formatDateSync(period);
            case 'week':
              const weekMatch = period.match(/(\d{4})-W(\d{2})/);
              if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
              return period;
            case 'month':
              const monthMatch = period.match(/(\d{4})-(\d{2})/);
              if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
              return period;
            case 'quarter':
              const quarterMatch = period.match(/(\d{4})-Q(\d)/);
              if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
              return period;
            case 'year':
              return period;
            default:
              return period;
          }
        } catch (error) {
          return period;
        }
      };

      const actualStartPeriod = startPeriod || (chartData.length > 0 ? chartData[0].period : '');
      const actualEndPeriod = endPeriod || (chartData.length > 0 ? chartData[chartData.length - 1].period : '');
      
      const addFooter = () => {
        const footerY = pageHeight - 10;
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const footerText = `Befundbericht, ${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() + 
                          `, Zeitraum ${formatPeriod(actualStartPeriod)} - ${formatPeriod(actualEndPeriod)}`;
        pdf.text(footerText, 20, footerY);
        pdf.setTextColor(0, 0, 0);
      };

      pdf.setFontSize(18);
      pdf.text(t('statistics.personal.pdf.title', 'Befund-Report'), 20, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      const userName = `${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
      pdf.text(`${t('statistics.personal.pdf.reportingPhysician', 'Befunder')}: ${userName}`, 20, yPosition);
      yPosition += 7;

      const timeFilterLabel = {
        date: t('statistics.personal.fallzahlen.day', 'Tag'),
        week: t('statistics.personal.fallzahlen.week', 'Woche'),
        month: t('statistics.personal.fallzahlen.month', 'Monat'),
        quarter: t('statistics.personal.fallzahlen.quarter', 'Quartal'),
        year: t('statistics.personal.fallzahlen.year', 'Jahr')
      }[timeFilter] || timeFilter;
      
      pdf.text(`${t('statistics.personal.pdf.interval', 'Intervall')}: ${timeFilterLabel}`, 20, yPosition);
      yPosition += 7;
      
      const periodText = `${t('statistics.personal.pdf.period', 'Zeitraum')}: ${formatPeriod(actualStartPeriod)} - ${formatPeriod(actualEndPeriod)}`;
      pdf.text(periodText, 20, yPosition);
      yPosition += 15;

      const chartWidth = 120;
      const chartHeight = (canvas.height * chartWidth) / canvas.width;
      const chartY = yPosition;
      pdf.addImage(chartImage, 'PNG', 20, yPosition, chartWidth, chartHeight);

      const legendX = 20 + chartWidth + 10;
      let legendY = chartY + 5;
      
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text(t('statistics.personal.pdf.legend', 'Legende') + ':', legendX, legendY);
      pdf.setFont('helvetica', 'normal');
      legendY += 4;

      // Nur aktive Serien in der Legende anzeigen (Farben aus CSS)
      const allLegendItems = [
        { active: showIndicationApprovals, label: t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'), color: chartColor1 },
        { active: showMyReports, label: t('statistics.personal.pdf.composedReports', 'Verfasste Befunde'), color: chartColor2 },
        { active: showForeignSignedReports, label: t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'), color: chartColor3 },
        { active: showReleasedReports, label: t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'), color: chartColor4 }
      ];
      
      const legendItems = allLegendItems.filter(item => item.active);

      legendItems.forEach(item => {
        pdf.setFillColor(item.color);
        pdf.rect(legendX, legendY - 2, 2, 2, 'F');
        pdf.setFontSize(6);
        pdf.text(item.label, legendX + 4, legendY);
        legendY += 4;
      });

      yPosition = chartY + chartHeight + 10;

      // Dynamische Tabellen-Header und -Daten basierend auf aktivierten Serien
      const tableHeaders = [t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')];
      if (showIndicationApprovals) tableHeaders.push(t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'));
      if (showMyReports) tableHeaders.push(t('statistics.personal.pdf.composedReports', 'Verfasste Befunde'));
      if (showForeignSignedReports) tableHeaders.push(t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'));
      if (showReleasedReports) tableHeaders.push(t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'));

      const tableData = chartData.map(row => {
        const rowData = [formatPeriod(row.period)];
        if (showIndicationApprovals) rowData.push(row.indicationCount);
        if (showMyReports) rowData.push(row.myReportsCount);
        if (showForeignSignedReports) rowData.push(row.foreignSignedCount);
        if (showReleasedReports) rowData.push(row.releasedCount);
        return rowData;
      });

      pdf.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
        margin: { left: 20, right: 20, bottom: 20 },
        styles: { 
          fontSize: 8,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        bodyStyles: {
          fillColor: [255, 255, 255]
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        didDrawPage: function(data) {
          addFooter();
        }
      });

      addFooter();

      const signatureLineHeight = 5 + 10;
      yPosition = pdf.lastAutoTable.finalY + 60;
      
      if (yPosition + signatureLineHeight > pageHeight - 20) {
        pdf.addPage();
        addFooter();
        yPosition = 50;
      }
      
      const signatureLineLength = 60;
      pdf.setFontSize(10);
      pdf.line(20, yPosition, 20 + signatureLineLength, yPosition);
      yPosition += 5;
      pdf.text(t('statistics.personal.pdf.signature', 'Unterschrift und Stempel'), 20, yPosition);
      yPosition += 15;

      const now = new Date();
      const generationDate = isFormatConfigLoaded ? formatDateSync(now.toISOString().split('T')[0]) : now.toLocaleDateString('de-DE');
      const generationTime = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      const electronicText = t('statistics.personal.pdf.electronicallyGenerated', 
        'Der Bericht wurde elektronisch erzeugt durch {{title}} {{name}} am {{date}} um {{time}}.',
        { 
          title: currentUser?.title || '',
          name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim(),
          date: generationDate,
          time: generationTime
        }
      );
      pdf.text(electronicText, 20, yPosition);

      pdf.save('cases_report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError(t('statistics.personal.pdf.error', 'Fehler beim Erstellen der PDF'), 'ERR-PDF-001');
    }
  };

  if (fallzahlenLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!fallzahlenData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.personal.fallzahlen.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  const getChartData = () => {
    const getDataForCategory = (category) => {
      let data = [];
      switch (timeFilter) {
        case 'date':
          data = category.by_date || [];
          break;
        case 'week':
          data = category.by_week || [];
          break;
        case 'month':
          data = category.by_month || [];
          break;
        case 'quarter':
          data = category.by_quarter || [];
          break;
        case 'year':
          data = category.by_year || [];
          break;
        default:
          data = category.by_month || [];
      }
      return data;
    };

    let myReportsData = getDataForCategory(fallzahlenData.my_reports);
    let foreignSignedData = getDataForCategory(fallzahlenData.foreign_signed_reports);
    let indicationData = getDataForCategory(fallzahlenData.indication_approvals);
    let releasedData = getDataForCategory(fallzahlenData.released_reports);

    const allPeriods = new Set([
      ...myReportsData.map(d => d.period),
      ...foreignSignedData.map(d => d.period),
      ...indicationData.map(d => d.period),
      ...releasedData.map(d => d.period)
    ]);

    const completeData = Array.from(allPeriods).sort().map(period => {
      const myReportsItem = myReportsData.find(d => d.period === period);
      const foreignSignedItem = foreignSignedData.find(d => d.period === period);
      const indicationItem = indicationData.find(d => d.period === period);
      const releasedItem = releasedData.find(d => d.period === period);

      return {
        period,
        myReportsCount: myReportsItem ? myReportsItem.count : 0,
        foreignSignedCount: foreignSignedItem ? foreignSignedItem.count : 0,
        indicationCount: indicationItem ? indicationItem.count : 0,
        releasedCount: releasedItem ? releasedItem.count : 0
      };
    });

    let filteredData = completeData;
    if (startPeriod && endPeriod) {
      const startIndex = completeData.findIndex(item => item.period === startPeriod);
      const endIndex = completeData.findIndex(item => item.period === endPeriod);
      
      if (startIndex !== -1 && endIndex !== -1 && startIndex <= endIndex) {
        filteredData = completeData.slice(startIndex, endIndex + 1);
      }
    } else if (startPeriod) {
      const startIndex = completeData.findIndex(item => item.period === startPeriod);
      if (startIndex !== -1) {
        filteredData = completeData.slice(startIndex);
      }
    } else if (endPeriod) {
      const endIndex = completeData.findIndex(item => item.period === endPeriod);
      if (endIndex !== -1) {
        filteredData = completeData.slice(0, endIndex + 1);
      }
    }

    return filteredData;
  };

  const chartData = getChartData();
  
  const availablePeriods = (() => {
    const getAllPeriods = () => {
      const periods = new Set();
      
      const addPeriods = (data) => {
        if (data) {
          data.forEach(d => periods.add(d.period));
        }
      };
      
      switch (timeFilter) {
        case 'date':
          addPeriods(fallzahlenData.my_reports.by_date);
          addPeriods(fallzahlenData.foreign_signed_reports.by_date);
          addPeriods(fallzahlenData.indication_approvals.by_date);
          addPeriods(fallzahlenData.released_reports.by_date);
          break;
        case 'week':
          addPeriods(fallzahlenData.my_reports.by_week);
          addPeriods(fallzahlenData.foreign_signed_reports.by_week);
          addPeriods(fallzahlenData.indication_approvals.by_week);
          addPeriods(fallzahlenData.released_reports.by_week);
          break;
        case 'month':
          addPeriods(fallzahlenData.my_reports.by_month);
          addPeriods(fallzahlenData.foreign_signed_reports.by_month);
          addPeriods(fallzahlenData.indication_approvals.by_month);
          addPeriods(fallzahlenData.released_reports.by_month);
          break;
        case 'quarter':
          addPeriods(fallzahlenData.my_reports.by_quarter);
          addPeriods(fallzahlenData.foreign_signed_reports.by_quarter);
          addPeriods(fallzahlenData.indication_approvals.by_quarter);
          addPeriods(fallzahlenData.released_reports.by_quarter);
          break;
        case 'year':
          addPeriods(fallzahlenData.my_reports.by_year);
          addPeriods(fallzahlenData.foreign_signed_reports.by_year);
          addPeriods(fallzahlenData.indication_approvals.by_year);
          addPeriods(fallzahlenData.released_reports.by_year);
          break;
        default:
          return [];
      }
      
      return Array.from(periods).sort().reverse();
    };
    
    return getAllPeriods();
  })();

  const formatPeriod = (period) => {
    if (!period) return period;
    
    try {
      switch (timeFilter) {
        case 'date':
          if (!isFormatConfigLoaded) return period;
          return formatDateSync(period);
        
        case 'week':
          const weekMatch = period.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `KW${weekMatch[2]} - ${weekMatch[1]}`;
          }
          return period;
        
        case 'month':
          const monthMatch = period.match(/(\d{4})-(\d{2})/);
          if (monthMatch) {
            return `${monthMatch[2]}.${monthMatch[1]}`;
          }
          return period;
        
        case 'quarter':
          const quarterMatch = period.match(/(\d{4})-Q(\d)/);
          if (quarterMatch) {
            return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
          }
          return period;
        
        case 'year':
          return period;
        
        default:
          return period;
      }
    } catch (error) {
      console.error('Error formatting period:', error);
      return period;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.personal.categories.fallzahlen', 'Fallzahlen')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3, flexWrap: 'nowrap' }}>
        <Grid container spacing={2} sx={{ flex: 1 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'var(--rd-chart-1)', color: 'white', height: '100%', width: '200px' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  {fallzahlenData.indication_approvals.total_count}
                </Typography>
                <Typography variant="body2" align="center">
                  {t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'var(--rd-chart-2)', color: 'white', height: '100%', width: '200px' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  {fallzahlenData.my_reports.total_count}
                </Typography>
                <Typography variant="body2" align="center">
                  {t('statistics.personal.fallzahlen.myReports', 'Meine Befunde')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'var(--rd-chart-3)', color: 'white', height: '100%',  width: '200px' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  {fallzahlenData.foreign_signed_reports.total_count}
                </Typography>
                <Typography variant="body2" align="center">
                  {t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'var(--rd-chart-4)', color: 'white', height: '100%',  width: '200px' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  {fallzahlenData.released_reports.total_count}
                </Typography>
                <Typography variant="body2" align="center">
                  {t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
              label={t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')}
            >
              <MenuItem value="date">{t('statistics.personal.fallzahlen.day', 'Tag')}</MenuItem>
              <MenuItem value="week">{t('statistics.personal.fallzahlen.week', 'Woche')}</MenuItem>
              <MenuItem value="month">{t('statistics.personal.fallzahlen.month', 'Monat')}</MenuItem>
              <MenuItem value="quarter">{t('statistics.personal.fallzahlen.quarter', 'Quartal')}</MenuItem>
              <MenuItem value="year">{t('statistics.personal.fallzahlen.year', 'Jahr')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.startPeriod', 'Von')}</InputLabel>
            <Select
              value={startPeriod}
              onChange={handleStartPeriodChange}
              label={t('statistics.personal.fallzahlen.startPeriod', 'Von')}
            >
              <MenuItem value="">{t('statistics.personal.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.endPeriod', 'Bis')}</InputLabel>
            <Select
              value={endPeriod}
              onChange={handleEndPeriodChange}
              label={t('statistics.personal.fallzahlen.endPeriod', 'Bis')}
            >
              <MenuItem value="">{t('statistics.personal.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('statistics.personal.fallzahlen.chartTitle', 'Entwicklung der Fallzahlen')}
          {' '}
          {timeFilter === 'date' && t('statistics.personal.fallzahlen.perDay', 'pro Tag')}
          {timeFilter === 'week' && t('statistics.personal.fallzahlen.perWeek', 'pro Woche')}
          {timeFilter === 'month' && t('statistics.personal.fallzahlen.perMonth', 'pro Monat')}
          {timeFilter === 'quarter' && t('statistics.personal.fallzahlen.perQuarter', 'pro Quartal')}
          {timeFilter === 'year' && t('statistics.personal.fallzahlen.perYear', 'pro Jahr')}
        </Typography>
        
        {!showMyReports && !showForeignSignedReports && !showIndicationApprovals && !showReleasedReports ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noSeriesSelected', 'Bitte wählen Sie mindestens eine Kategorie aus')}
          </Typography>
        ) : chartData.length > 0 ? (
          <Box ref={chartRef} sx={{ width: '100%', height: 400 }}>
            <LineChart
              dataset={chartData}
              xAxis={[{ 
                dataKey: 'period',
                scaleType: 'point',
                tickLabelInterval: chartData.length > 30 ? Math.floor(chartData.length / 20) : 0,
                valueFormatter: (value) => formatPeriod(value)
              }]}
              yAxis={[{
                min: 0,
                max: (() => {
                  // Finde den maximalen Wert aus allen aktiven Serien
                  const maxValue = Math.max(
                    ...chartData.map(d => Math.max(
                      showIndicationApprovals ? d.indicationCount : 0,
                      showMyReports ? d.myReportsCount : 0,
                      showForeignSignedReports ? d.foreignSignedCount : 0,
                      showReleasedReports ? d.releasedCount : 0
                    ))
                  );
                  // Füge 10% Puffer hinzu
                  return Math.ceil(maxValue * 1.1);
                })()
              }]}
              series={[
                ...(showIndicationApprovals ? [{
                  type: 'line',
                  dataKey: 'indicationCount',
                  label: t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'),
                  color: 'var(--rd-chart-1)',
                  curve: 'natural',
                  area: true,
                  showMark: false,
                  valueFormatter: (value) => `${value}`
                }] : []),
                ...(showMyReports ? [{
                  type: 'line',
                  dataKey: 'myReportsCount',
                  label: t('statistics.personal.fallzahlen.myReports', 'Meine Befunde'),
                  color: 'var(--rd-chart-2)',
                  curve: 'natural',
                  area: true,
                  showMark: false,
                  valueFormatter: (value) => `${value}`
                }] : []),
                ...(showForeignSignedReports ? [{
                  type: 'line',
                  dataKey: 'foreignSignedCount',
                  label: t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'),
                  color: 'var(--rd-chart-3)',
                  curve: 'natural',
                  area: true,
                  showMark: false,
                  valueFormatter: (value) => `${value}`
                }] : []),
                ...(showReleasedReports ? [{
                  type: 'line',
                  dataKey: 'releasedCount',
                  label: t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'),
                  color: 'var(--rd-chart-4)',
                  curve: 'natural',
                  area: true,
                  showMark: false,
                  valueFormatter: (value) => `${value}`
                }] : [])
              ]}
              height={450}
              margin={{ top: 20, right: 30, bottom: 80, left: 70 }}
              grid={{ vertical: false, horizontal: true }}
              tooltip={{
                trigger: 'axis'
              }}
              sx={{
                '.MuiLineElement-root': {
                  strokeWidth: 2.5
                },
                '.MuiAreaElement-root': {
                  fillOpacity: 0.3
                },
                '.MuiMarkElement-root': {
                  scale: '0.9',
                  fill: 'var(--rd-primary-contrast)',
                  strokeWidth: 2
                },
                '.MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                  transform: 'translateY(10px) rotate(-45deg)',
                  textAnchor: 'end !important'
                },
                '.MuiChartsLegend-root': {
                  display: 'none !important'
                }
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noChartData', 'Keine Daten für die ausgewählten Filter verfügbar')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={showIndicationApprovals} 
                onChange={(e) => setShowIndicationApprovals(e.target.checked)}
                sx={{ color: 'var(--rd-chart-1)', '&.Mui-checked': { color: 'var(--rd-chart-1)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showMyReports} 
                onChange={(e) => setShowMyReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-2)', '&.Mui-checked': { color: 'var(--rd-chart-2)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.myReports', 'Meine Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showForeignSignedReports} 
                onChange={(e) => setShowForeignSignedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-3)', '&.Mui-checked': { color: 'var(--rd-chart-3)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showReleasedReports} 
                onChange={(e) => setShowReleasedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-4)', '&.Mui-checked': { color: 'var(--rd-chart-4)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
          />
        </Box>
      </Paper>
    </Box>
  );
});

Cases.displayName = 'Cases';

export default Cases;
